import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  generateRandomId,
  getSelfProducedFertilizerIdsByYear,
  isFertilizerSourceComplete,
  toFertilizerSourceValues,
  useCreateFarmOrganicFertilizerProduction,
  useDeleteFarmOrganicFertilizerProduction,
  useGetAllFieldPracticesByFarm,
  useGetFarmOrganicFertilizerProductionByYear,
  useGetFarms,
  useGetFieldsByFarmId,
  useGetPracticeOrganicFertilizers,
  useTranslation,
  useUpdateFarmOrganicFertilizerProduction,
} from "utils";

import { ANIMAL_BASED_FERTILIZER_SLUGS, BIOMASS_INPUT_TYPE_OPTIONS, QUANTITY_UNIT_OPTIONS } from "consts";

import { CooperationItemRequest, PracticeFertilizerOrganic, QuantityUnit, WithInjectedName } from "models";

import {
  Button,
  Drawer,
  Flex,
  Form,
  Icon,
  IconButton,
  InputNumber,
  InputNumberWithUnitSelector,
  Link,
  Option,
  Select,
  Switch,
  Tag,
  TextArea,
  Tooltip,
  TooltipWrapper,
  Typography,
} from "components";

type FarmSourcesDrawerProps = {
  visible: boolean;
  onClose: () => void;
};

type OwnProductionItemDraft = {
  tempId: string;
  biomassInputType: string | null;
  quantity: number | null;
  quantityUnit: QuantityUnit | null;
  comment: string | null;
};

export type OwnProductionValues = {
  enabled: boolean;
  livestockCount: number | null; // animal only
  comment: string | null; // animal only
  items: OwnProductionItemDraft[]; // non-animal only
};

type CooperationItemDraft = {
  tempId: string;
  exportedBiomassType: string | null;
  quantity: number | null;
  quantityUnit: QuantityUnit | null;
  comment: string | null;
};

export type CooperationValues = {
  enabled: boolean;
  items: CooperationItemDraft[];
};

export type FertilizerSourceValues = {
  ownProduction: OwnProductionValues;
  cooperation: CooperationValues;
};

const EMPTY_OWN_PRODUCTION: OwnProductionValues = {
  enabled: false,
  livestockCount: null,
  comment: null,
  items: [],
};

const EMPTY_COOPERATION: CooperationValues = {
  enabled: false,
  items: [],
};

const EMPTY_CARD_VALUES: FertilizerSourceValues = {
  ownProduction: EMPTY_OWN_PRODUCTION,
  cooperation: EMPTY_COOPERATION,
};

function newCoopItem(): CooperationItemDraft {
  return { tempId: generateRandomId(), exportedBiomassType: null, quantity: null, quantityUnit: null, comment: null };
}

function newOwnItem(): OwnProductionItemDraft {
  return { tempId: generateRandomId(), biomassInputType: null, quantity: null, quantityUnit: null, comment: null };
}

type PrefillOption = { year: number; values: FertilizerSourceValues };

type FertilizerSourceCardProps = {
  fertilizerId: string;
  fertilizer: WithInjectedName<PracticeFertilizerOrganic> | undefined;
  fieldNames: string[];
  formValues: FertilizerSourceValues;
  onChange: (values: FertilizerSourceValues) => void;
  showErrors: boolean;
  validationTick: number;
  prefillOptions: PrefillOption[];
  expanded: boolean;
  onToggle: () => void;
};

const FieldError = ({ message }: { message: string }) => (
  <Typography variant="paragraph-s" color="red-500" className="margin-0">
    {message}
  </Typography>
);

const FertilizerSourceCard = ({
  fertilizerId,
  fertilizer,
  fieldNames,
  formValues,
  onChange,
  showErrors,
  validationTick,
  prefillOptions,
  expanded,
  onToggle,
}: FertilizerSourceCardProps) => {
  const { t } = useTranslation("allFieldView");
  const [cardForm] = Form.useForm();
  const isAnimalBased = fertilizer ? ANIMAL_BASED_FERTILIZER_SLUGS.has(fertilizer.translationSlug) : false;
  const icon = isAnimalBased ? "klim/slurry" : "klim/compost";

  const visibleFields = fieldNames.slice(0, 2);
  const overflowFields = fieldNames.slice(2);
  const overflowCount = overflowFields.length;

  const { ownProduction, cooperation } = formValues;
  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;

  useEffect(() => {
    cardForm.setFieldsValue({
      livestockCount: ownProduction.livestockCount ?? undefined,
      comment: ownProduction.comment ?? undefined,
    });
  }, [ownProduction.enabled, ownProduction.livestockCount, ownProduction.comment]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showErrors) return;
    const { ownProduction: own, cooperation: coop } = formValuesRef.current;
    const req = [t("farmSourcesDrawer.validation.required")];
    const ok: string[] = [];

    cardForm.setFields([
      { name: "livestockCount", errors: own.enabled && isAnimalBased && !own.livestockCount ? req : ok },
      ...own.items.flatMap((item) => [
        { name: `ownBiomassType-${item.tempId}`, errors: own.enabled && !isAnimalBased && !item.biomassInputType ? req : ok },
        { name: `ownQuantity-${item.tempId}`, errors: own.enabled && !isAnimalBased && !item.quantity ? req : ok },
        { name: `ownQuantity-${item.tempId}-unit`, errors: own.enabled && !isAnimalBased && !item.quantityUnit ? req : ok },
        { name: `ownComment-${item.tempId}`, errors: own.enabled && !isAnimalBased && item.biomassInputType === "other" && !item.comment ? req : ok },
      ]),
      ...coop.items.flatMap((item) => [
        { name: `coopBiomassType-${item.tempId}`, errors: !item.exportedBiomassType ? req : ok },
        { name: `coopQuantity-${item.tempId}`, errors: !item.quantity ? req : ok },
        { name: `coopQuantity-${item.tempId}-unit`, errors: !item.quantityUnit ? req : ok },
        { name: `coopComment-${item.tempId}`, errors: item.exportedBiomassType === "other" && !item.comment ? req : ok },
      ]),
    ]);
  }, [showErrors, validationTick, isAnimalBased]); // eslint-disable-line react-hooks/exhaustive-deps

  const isComplete = isFertilizerSourceComplete(formValues, isAnimalBased);

  const patchOwn = (patch: Partial<OwnProductionValues>) => onChange({ ...formValues, ownProduction: { ...ownProduction, ...patch } });

  const patchCoop = (patch: Partial<CooperationValues>) => onChange({ ...formValues, cooperation: { ...cooperation, ...patch } });

  const toggleOwnProduction = (enabled: boolean) => {
    if (enabled) {
      patchOwn({ enabled: true, items: !isAnimalBased && ownProduction.items.length === 0 ? [newOwnItem()] : ownProduction.items });
    } else {
      patchOwn({ enabled: false });
    }
  };

  const toggleCooperation = (enabled: boolean) => {
    if (enabled) {
      patchCoop({ enabled: true, items: cooperation.items.length === 0 ? [newCoopItem()] : cooperation.items });
    } else {
      patchCoop({ enabled: false });
    }
  };

  const updateOwnItem = (tempId: string, patch: Partial<OwnProductionItemDraft>) => {
    patchOwn({ items: ownProduction.items.map((item) => (item.tempId === tempId ? { ...item, ...patch } : item)) });
  };

  const removeOwnItem = (tempId: string) => {
    patchOwn({ items: ownProduction.items.filter((item) => item.tempId !== tempId) });
  };

  const addOwnItem = () => {
    patchOwn({ items: [...ownProduction.items, newOwnItem()] });
  };

  const updateCoopItem = (tempId: string, patch: Partial<CooperationItemDraft>) => {
    patchCoop({ items: cooperation.items.map((item) => (item.tempId === tempId ? { ...item, ...patch } : item)) });
  };

  const removeCoopItem = (tempId: string) => {
    patchCoop({ items: cooperation.items.filter((item) => item.tempId !== tempId) });
  };

  const addCoopItem = () => {
    patchCoop({ items: [...cooperation.items, newCoopItem()] });
  };

  return (
    <Flex vertical className="fertilizer-source-card">
      {/* Header row */}
      <Flex align="center" justify="space-between" gap="1rem" className="fertilizer-source-card-header" onClick={onToggle}>
        <Flex align="center" gap="0.75rem" style={{ flex: 1, minWidth: 0 }}>
          <div className="fertilizer-source-card-icon">
            <Flex align="center" justify="center" className="fertilizer-source-card-icon-flex">
              <Icon iconName={icon} size="m" />
            </Flex>
          </div>
          <Flex vertical gap="0.25rem" style={{ minWidth: 0 }}>
            <Typography variant="h5" bold className="margin-0">
              {fertilizer?.name ?? fertilizerId}
            </Typography>
            {fieldNames.length > 0 && (
              <Flex align="center" gap="0.5rem" wrap="nowrap" className="header-applied-on">
                <Typography variant="paragraph-s" color="grey-600" className="margin-0 header-applied-on-label">
                  {t("farmSourcesDrawer.appliedOn")}
                </Typography>
                {visibleFields.map((name) => (
                  <TooltipWrapper key={name} tooltip={{ title: name, placement: "top" }}>
                    <Typography className="applied-on-field-btn" variant="paragraph-m">
                      {name}
                    </Typography>
                  </TooltipWrapper>
                ))}
                {overflowCount > 0 && (
                  <Tooltip title={overflowFields.join(", ")}>
                    <span className="applied-on-field-btn">{`+${overflowCount}`}</span>
                  </Tooltip>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
        <Tag colour={isComplete ? "green" : "red"} text={t(isComplete ? "farmSourcesDrawer.complete" : "farmSourcesDrawer.incomplete")} />
        <Icon iconName={expanded ? "tabler/chevron-up" : "tabler/chevron-down"} size="s" />
      </Flex>

      {/* Expanded body */}
      {expanded && (
        <Form form={cardForm}>
          <Flex vertical className="fertilizer-source-card-body">
            {/* Prefill options */}
            {prefillOptions.length > 0 && (
              <Flex align="center" justify="space-between" className="prefill-row">
                <Typography variant="paragraph-s" color="grey-600" className="margin-0 prefill-label">
                  {t("farmSourcesDrawer.prefillFrom")}
                </Typography>
                <Flex gap=".5rem">
                  {prefillOptions.map((opt) => (
                    <Button key={opt.year} type="text" size="small" className="prefill-year-btn" onClick={() => onChange(opt.values)}>
                      {opt.year}
                    </Button>
                  ))}
                </Flex>
              </Flex>
            )}

            {/* No source selected error */}
            {showErrors && !ownProduction.enabled && !cooperation.enabled && (
              <div className="source-section-error">
                <FieldError message={t("farmSourcesDrawer.validation.sourceRequired")} />
              </div>
            )}

            {/* Own production section */}
            <Flex vertical gap="0.75rem" className="source-section">
              <Flex align="center" justify="space-between">
                <Typography variant="paragraph-m" className="margin-0">
                  {t("farmSourcesDrawer.ownProduction.sectionTitle")}
                </Typography>
                <Switch checked={ownProduction.enabled} onChange={toggleOwnProduction} />
              </Flex>

              {ownProduction.enabled && (
                <Flex vertical gap="0.75rem" className="source-section-fields">
                  {isAnimalBased ? (
                    <>
                      <InputNumber
                        name="livestockCount"
                        label={t("farmSourcesDrawer.livestockCount.label")}
                        value={ownProduction.livestockCount ?? undefined}
                        initialValue={ownProduction.livestockCount ?? undefined}
                        rules={[{ required: true, message: t("farmSourcesDrawer.validation.required") }]}
                        onChange={(val) => patchOwn({ livestockCount: val })}
                        min={1}
                        step={1}
                        decimalSeparator=","
                      />
                      <TextArea
                        key="animal-comment"
                        name="comment"
                        label={t("farmSourcesDrawer.comment.label")}
                        placeholder={t("farmSourcesDrawer.comment.placeholderAnimal")}
                        initialValue={ownProduction.comment}
                        onChange={(e) => patchOwn({ comment: e.target.value || null })}
                        autoSize={{ minRows: 2, maxRows: 2 }}
                      />
                    </>
                  ) : (
                    <Flex vertical gap="0.75rem">
                      {ownProduction.items.length === 0 && (
                        <FieldError message={showErrors ? t("farmSourcesDrawer.validation.atLeastOneItem") : t("farmSourcesDrawer.ownProductionItems.empty")} />
                      )}
                      {ownProduction.items.map((item, index) => (
                        <Flex key={item.tempId} vertical gap="0.5rem" className="biomass-item">
                          <Flex align="center" justify="space-between">
                            <Typography variant="paragraph-m" bold className="margin-0">
                              {t("farmSourcesDrawer.ownProductionItems.itemTitle")} {index + 1}
                            </Typography>
                            <IconButton size="small" type="tertiary" iconName="tabler/trash" onClick={() => removeOwnItem(item.tempId)} />
                          </Flex>
                          <Select
                            name={`ownBiomassType-${item.tempId}`}
                            label={t("farmSourcesDrawer.biomassInputType.label")}
                            value={item.biomassInputType ?? null}
                            initialValue={item.biomassInputType ?? undefined}
                            rules={[{ required: true, message: t("farmSourcesDrawer.validation.required") }]}
                            onChange={(val: string | null) => updateOwnItem(item.tempId, { biomassInputType: val })}
                          >
                            {BIOMASS_INPUT_TYPE_OPTIONS.map((opt) => (
                              <Option key={opt} value={opt} label={[t(`farmSourcesDrawer.biomassInputType.${opt}`)]}>
                                {t(`farmSourcesDrawer.biomassInputType.${opt}`)}
                              </Option>
                            ))}
                          </Select>
                          <InputNumberWithUnitSelector
                            label={t("farmSourcesDrawer.quantity.label")}
                            name={`ownQuantity-${item.tempId}`}
                            rules={[{ required: true, message: t("farmSourcesDrawer.validation.required") }]}
                            value={item.quantity ?? undefined}
                            initialValue={item.quantity ?? undefined}
                            unitValue={item.quantityUnit}
                            units={[...QUANTITY_UNIT_OPTIONS]}
                            unitLabel={t("farmSourcesDrawer.quantityUnit.label")}
                            min={0}
                            onChange={(val, unit) => updateOwnItem(item.tempId, { quantity: val, quantityUnit: unit as QuantityUnit | null })}
                          />
                          {item.biomassInputType === "other" && (
                            <TextArea
                              key={`own-comment-${item.tempId}`}
                              name={`ownComment-${item.tempId}`}
                              label={t("farmSourcesDrawer.comment.labelRequired")}
                              placeholder={t("farmSourcesDrawer.comment.placeholderNonAnimal")}
                              rules={[{ required: true, message: t("farmSourcesDrawer.validation.required") }]}
                              initialValue={item.comment}
                              onChange={(e) => updateOwnItem(item.tempId, { comment: e.target.value || null })}
                              autoSize={{ minRows: 2, maxRows: 2 }}
                            />
                          )}
                        </Flex>
                      ))}
                      <Link onClick={addOwnItem}>
                        <Icon iconName="klim/plus" className="icon inline" /> {t("farmSourcesDrawer.ownProductionItems.addRow")}
                      </Link>
                    </Flex>
                  )}
                </Flex>
              )}
            </Flex>

            {/* Cooperation section */}
            <Flex vertical gap="0.75rem" className="source-section">
              <Flex align="center" justify="space-between">
                <Typography variant="paragraph-m" className="margin-0">
                  {t("farmSourcesDrawer.cooperation.sectionTitle")}
                </Typography>
                <Switch checked={cooperation.enabled} onChange={toggleCooperation} />
              </Flex>

              {cooperation.enabled && (
                <Flex vertical gap="0.75rem" className="source-section-fields">
                  {cooperation.items.length === 0 && (
                    <FieldError message={showErrors ? t("farmSourcesDrawer.validation.atLeastOneItem") : t("farmSourcesDrawer.cooperationItems.empty")} />
                  )}
                  {cooperation.items.map((item, index) => (
                    <Flex key={item.tempId} vertical gap="0.5rem" className="biomass-item">
                      <Flex align="center" justify="space-between">
                        <Typography variant="paragraph-m" bold className="margin-0">
                          {t("farmSourcesDrawer.cooperationItems.itemTitle")} {index + 1}
                        </Typography>
                        <IconButton size="small" type="tertiary" iconName="tabler/trash" onClick={() => removeCoopItem(item.tempId)} />
                      </Flex>
                      <Select
                        name={`coopBiomassType-${item.tempId}`}
                        label={t("farmSourcesDrawer.biomassInputType.label")}
                        value={item.exportedBiomassType ?? null}
                        initialValue={item.exportedBiomassType ?? undefined}
                        rules={[{ required: true, message: t("farmSourcesDrawer.validation.required") }]}
                        onChange={(val: string | null) => updateCoopItem(item.tempId, { exportedBiomassType: val })}
                      >
                        {BIOMASS_INPUT_TYPE_OPTIONS.map((opt) => (
                          <Option key={opt} value={opt} label={[t(`farmSourcesDrawer.biomassInputType.${opt}`)]}>
                            {t(`farmSourcesDrawer.biomassInputType.${opt}`)}
                          </Option>
                        ))}
                      </Select>
                      <InputNumberWithUnitSelector
                        label={t("farmSourcesDrawer.quantity.label")}
                        name={`coopQuantity-${item.tempId}`}
                        rules={[{ required: true, message: t("farmSourcesDrawer.validation.required") }]}
                        value={item.quantity ?? undefined}
                        initialValue={item.quantity ?? undefined}
                        unitValue={item.quantityUnit}
                        units={[...QUANTITY_UNIT_OPTIONS]}
                        unitLabel={t("farmSourcesDrawer.quantityUnit.label")}
                        min={0}
                        onChange={(val, unit) => updateCoopItem(item.tempId, { quantity: val, quantityUnit: unit as QuantityUnit | null })}
                      />
                      {item.exportedBiomassType === "other" && (
                        <TextArea
                          key={`coop-comment-${item.tempId}`}
                          name={`coopComment-${item.tempId}`}
                          label={t("farmSourcesDrawer.comment.labelRequired")}
                          placeholder={t("farmSourcesDrawer.comment.placeholderNonAnimal")}
                          rules={[{ required: true, message: t("farmSourcesDrawer.validation.required") }]}
                          initialValue={item.comment}
                          onChange={(e) => updateCoopItem(item.tempId, { comment: e.target.value || null })}
                          autoSize={{ minRows: 2, maxRows: 2 }}
                        />
                      )}
                    </Flex>
                  ))}
                  <Link onClick={addCoopItem}>
                    <Icon iconName="klim/plus" className="icon inline" /> {t("farmSourcesDrawer.cooperationItems.addRow")}
                  </Link>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Form>
      )}
    </Flex>
  );
};

export const FarmSourcesDrawer = ({ visible, onClose }: FarmSourcesDrawerProps) => {
  const { t } = useTranslation("allFieldView");
  const { currentFarm } = useGetFarms();
  const farmId = currentFarm?.id ?? "";

  const [selectedYear, setSelectedYear] = useState<number>(0);

  const { data: allFieldsPractices, isLoading: isLoadingPractices } = useGetAllFieldPracticesByFarm(currentFarm);
  const { data: fields, isLoading: isLoadingFields } = useGetFieldsByFarmId({ farmId });
  const { data: organicFertilizers, isLoading: isLoadingFertilizers } = useGetPracticeOrganicFertilizers({});

  const { mutateAsync: createProduction } = useCreateFarmOrganicFertilizerProduction({ farmId, year: selectedYear });
  const { mutateAsync: updateProduction } = useUpdateFarmOrganicFertilizerProduction({ farmId, year: selectedYear });
  const { mutateAsync: deleteProduction } = useDeleteFarmOrganicFertilizerProduction({ farmId, year: selectedYear });

  // Local edits only, per year and fertilizer. Untouched cards render straight from the query data so they never go stale.
  const [formStatesByYear, setFormStatesByYear] = useState<Record<number, Record<string, FertilizerSourceValues>>>({});
  const [openCardIds, setOpenCardIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [validationTick, setValidationTick] = useState(0);

  const fertilizerIdsByYear = useMemo(() => getSelfProducedFertilizerIdsByYear(allFieldsPractices), [allFieldsPractices]);
  const availableYears = useMemo(
    () =>
      Object.keys(fertilizerIdsByYear)
        .map(Number)
        .sort((a, b) => a - b),
    [fertilizerIdsByYear]
  );

  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[availableYears.length - 1]);
    }
  }, [availableYears, selectedYear]);

  const { productionsByYear } = useGetFarmOrganicFertilizerProductionByYear({ farmId, years: availableYears });

  const isLoadingProductions = productionsByYear[selectedYear] === undefined && !!farmId && !!selectedYear && availableYears.includes(selectedYear);

  const selfProducedFertilizerIds = fertilizerIdsByYear[selectedYear] ?? [];

  const hasDataForYear = (year: number) => productionsByYear[year] !== undefined || year in formStatesByYear;

  const getCardValues = (year: number, fertilizerId: string): FertilizerSourceValues =>
    formStatesByYear[year]?.[fertilizerId] ?? (productionsByYear[year] ? toFertilizerSourceValues(productionsByYear[year], fertilizerId) : EMPTY_CARD_VALUES);

  const isCardComplete = (fertilizerId: string, values: FertilizerSourceValues) => {
    const fertilizer = organicFertilizers?.find((f) => f.id === fertilizerId);
    return isFertilizerSourceComplete(values, fertilizer ? ANIMAL_BASED_FERTILIZER_SLUGS.has(fertilizer.translationSlug) : false);
  };

  const countIncompleteCards = (year: number) =>
    hasDataForYear(year)
      ? (fertilizerIdsByYear[year] ?? []).filter((fertilizerId) => !isCardComplete(fertilizerId, getCardValues(year, fertilizerId))).length
      : 0;

  const incompleteYears = new Set(availableYears.filter((year) => countIncompleteCards(year) > 0));

  const prefillOptionsByFertilizer: Record<string, PrefillOption[]> = {};
  availableYears
    .filter((year) => year !== selectedYear && hasDataForYear(year))
    .forEach((year) => {
      (fertilizerIdsByYear[year] ?? []).forEach((fertilizerId) => {
        const values = getCardValues(year, fertilizerId);
        if (!values.ownProduction.enabled && !values.cooperation.enabled) return;
        if (!prefillOptionsByFertilizer[fertilizerId]) prefillOptionsByFertilizer[fertilizerId] = [];
        prefillOptionsByFertilizer[fertilizerId].push({ year, values });
      });
    });

  const getFieldNamesForFertilizer = (fertilizerId: string): string[] => {
    if (!fields || !allFieldsPractices) return [];
    return Object.entries(allFieldsPractices)
      .filter(([_, fp]) => fp.practices.some((p) => p.assignedPracticeId === fertilizerId && p.selfProduced === true && p.year === selectedYear))
      .map(([fieldId]) => fields.find((f) => f.id === fieldId)?.name ?? "")
      .filter(Boolean);
  };

  const selectYear = (year: number) => {
    setSelectedYear(year);
    setShowErrors(false);
  };

  const saveCard = async (year: number, fertilizerId: string) => {
    const values = getCardValues(year, fertilizerId);
    const yearProductions = productionsByYear[year] ?? [];
    const ownProdRecord = yearProductions.find((p) => p.fertilizerId === fertilizerId && p.sourceType === "own_production");
    const coopRecord = yearProductions.find((p) => p.fertilizerId === fertilizerId && p.sourceType === "cooperation");

    // own_production
    if (values.ownProduction.enabled) {
      const fertilizer = organicFertilizers?.find((f) => f.id === fertilizerId);
      const isAnimalBased = fertilizer ? ANIMAL_BASED_FERTILIZER_SLUGS.has(fertilizer.translationSlug) : false;
      const ownProductionItems = !isAnimalBased
        ? values.ownProduction.items
            .filter((item) => item.biomassInputType && item.quantity && item.quantityUnit)
            .map((item) => ({
              biomassInputType: item.biomassInputType!,
              quantity: item.quantity!,
              quantityUnit: item.quantityUnit!,
              comment: item.comment,
            }))
        : undefined;
      const payload = {
        farmId,
        sourceType: "own_production" as const,
        livestockCount: isAnimalBased ? values.ownProduction.livestockCount ?? undefined : undefined,
        comment: isAnimalBased ? values.ownProduction.comment : undefined,
        ownProductionItems,
      };
      if (ownProdRecord) {
        await updateProduction({ ...payload, productionId: ownProdRecord.id, year });
      } else {
        await createProduction({ ...payload, year, fertilizerId });
      }
    } else if (ownProdRecord) {
      await deleteProduction({ farmId, productionId: ownProdRecord.id, year });
    }

    // cooperation
    if (values.cooperation.enabled) {
      const cooperationItems: CooperationItemRequest[] = values.cooperation.items
        .filter((item) => item.exportedBiomassType && item.quantity && item.quantityUnit && (item.exportedBiomassType !== "other" || item.comment))
        .map((item) => ({
          exportedBiomassType: item.exportedBiomassType!,
          quantity: item.quantity!,
          quantityUnit: item.quantityUnit!,
          comment: item.comment,
        }));
      const payload = { farmId, sourceType: "cooperation" as const, cooperationItems };
      if (coopRecord) {
        await updateProduction({ ...payload, productionId: coopRecord.id, year });
      } else {
        await createProduction({ ...payload, year, fertilizerId });
      }
    } else if (coopRecord) {
      await deleteProduction({ farmId, productionId: coopRecord.id, year });
    }
  };

  const handleSave = async () => {
    // Persist every year with local edits, not just the visible tab: edits on other tabs (e.g. prefilled from another year) were otherwise lost on remount
    const yearsToSave = [...new Set([selectedYear, ...Object.keys(formStatesByYear).map(Number)])];

    // Only validate cards where the user has started filling in data (at least one toggle enabled)
    const firstInvalidYear = yearsToSave.find((year) =>
      (fertilizerIdsByYear[year] ?? []).some((fertilizerId) => {
        const values = getCardValues(year, fertilizerId);
        return (values.ownProduction.enabled || values.cooperation.enabled) && !isCardComplete(fertilizerId, values);
      })
    );

    if (firstInvalidYear !== undefined) {
      setSelectedYear(firstInvalidYear);
      setShowErrors(true);
      setValidationTick((t) => t + 1);
      return;
    }

    setShowErrors(false);
    setIsSaving(true);
    try {
      await Promise.all(yearsToSave.flatMap((year) => (fertilizerIdsByYear[year] ?? []).map((fertilizerId) => saveCard(year, fertilizerId))));
    } finally {
      setIsSaving(false);
    }
    setFormStatesByYear({});
    onClose();
  };

  const isLoading = isLoadingPractices || isLoadingFields || isLoadingFertilizers || isLoadingProductions;

  return (
    <Drawer
      title={t("farmSourcesDrawer.drawerTitle", { year: selectedYear })}
      visible={visible}
      onClose={onClose}
      loading={isLoading}
      footer={
        <Flex gap="0.5rem">
          <Button type="secondary" block onClick={onClose}>
            {t("farmSourcesDrawer.footer.goBack")}
          </Button>
          <Button type="primary" block loading={isSaving} onClick={handleSave}>
            {t("farmSourcesDrawer.footer.saveAndClose")}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap="1.5rem">
        {/* Intro */}
        <Flex vertical gap="1rem">
          <Typography variant="h1" className="margin-0">
            {t("farmSourcesDrawer.heading")}
          </Typography>
          <Typography variant="paragraph-m" className="margin-0">
            {t("farmSourcesDrawer.description")}
          </Typography>
        </Flex>

        {/* Year tabs */}
        {availableYears.length > 1 && (
          <div className="farm-sources-year-tabs">
            {availableYears.map((year) => (
              <div key={year} role="tab" className={`farm-sources-year-tab${selectedYear === year ? " active" : ""}`} onClick={() => selectYear(year)}>
                {year}
                {incompleteYears.has(year) && <span className="farm-sources-year-tab-dot" />}
              </div>
            ))}
          </div>
        )}

        {/* Cards */}
        <Form>
          <Flex vertical gap="1rem">
            {selfProducedFertilizerIds.map((fertilizerId) => (
              <FertilizerSourceCard
                key={`${fertilizerId}-${selectedYear}`}
                fertilizerId={fertilizerId}
                fertilizer={organicFertilizers?.find((f) => f.id === fertilizerId)}
                fieldNames={getFieldNamesForFertilizer(fertilizerId)}
                formValues={getCardValues(selectedYear, fertilizerId)}
                onChange={(vals) => setFormStatesByYear((prev) => ({ ...prev, [selectedYear]: { ...(prev[selectedYear] ?? {}), [fertilizerId]: vals } }))}
                prefillOptions={prefillOptionsByFertilizer[fertilizerId] ?? []}
                showErrors={showErrors}
                validationTick={validationTick}
                expanded={openCardIds.has(fertilizerId)}
                onToggle={() =>
                  setOpenCardIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(fertilizerId)) next.delete(fertilizerId);
                    else next.add(fertilizerId);
                    return next;
                  })
                }
              />
            ))}
          </Flex>
        </Form>
      </Flex>
    </Drawer>
  );
};
